import { useState, useCallback } from 'react'
import { useQueryClient } from 'react-query'
import { BASE_API_URL } from 'constants/env'
import { API_METHODS } from 'constants/api-methods'
import { apiHeaders } from 'utils/api-headers'
import { store } from 'index'
import { handleErrors } from 'utils/handle-errors'
import { Ansible } from 'interfaces/ansible'
import { PhysicalPhone, PhoneStatus } from 'interfaces/physical-phone'
import toast from 'react-hot-toast'

interface PhysicalPhoneAssignmentData {
  phone: PhysicalPhone
  newName: string
  assignedMacMiniNumber: number
  ansible: Ansible
}

interface UsePhysicalPhoneAssignmentReturn {
  assignPhonesToAnsible: (
    phones: PhysicalPhone[],
    ansible: Ansible,
    terminalId: string
  ) => Promise<void>
  isAssigning: boolean
  assignmentProgress: {
    current: number
    total: number
    currentAction: string
    previewNames?: string[]
  } | null
}

// Helper function to extract terminal prefix from terminal name (e.g., "T000" -> "", "T001" -> "T001")
const extractTerminalPrefix = (terminalName: string): string => {
  const terminal = terminalName || 'T000'
  return terminal === 'T000' ? '' : terminal // Omit T000 prefix
}

// Helper function to extract ansible number from ansible name (e.g., "A6" -> "6", "A17" -> "17")
const extractAnsibleNumber = (ansibleName: string): string => {
  const match = ansibleName.match(/A(\d+)/)
  return match ? match[1] : '0' // No zero padding
}

// Helper function to extract phone number from phone name (e.g., "A1M5P23" -> 23)
const extractPhoneNumber = (phoneName: string): number => {
  const match = phoneName.match(/P(\d+)$/)
  return match ? parseInt(match[1], 10) : 0
}

// Helper function to find the highest phone number in an ansible
const findHighestPhoneNumber = (existingPhones: PhysicalPhone[]): number => {
  if (existingPhones.length === 0) return 0

  const phoneNumbers = existingPhones
    .map((phone) => extractPhoneNumber(phone.name))
    .filter((num) => num > 0)

  return phoneNumbers.length > 0 ? Math.max(...phoneNumbers) : 0
}

// Helper function to calculate Mac Mini number from phone number (sequential: 1 -> M1, 2 -> M1, etc.)
// Note: This is now simplified for sequential Mac Mini User naming (P1, P2, P3...)
const calculateMacMiniNumber = (phoneNumber: number): number => {
  return Math.ceil(phoneNumber / 5) // Keep same Mac Mini grouping logic for now
}

// Helper function to generate new Physical Phone name
const generatePhoneName = (
  terminalName: string,
  ansibleName: string,
  phoneNumber: number
): string => {
  const terminalPrefix = extractTerminalPrefix(terminalName)
  const ansibleNumber = extractAnsibleNumber(ansibleName)
  const macMiniNumber = calculateMacMiniNumber(phoneNumber)
  const terminalPart = terminalPrefix ? `${terminalPrefix}` : ''

  return `${terminalPart}A${ansibleNumber}M${macMiniNumber}P${phoneNumber}`
}

// Function to update a single Physical Phone
const updatePhysicalPhone = async (
  phoneId: string,
  updates: { name: string; status: PhoneStatus; ansibleId: string }
) => {
  let response = await fetch(
    `${BASE_API_URL}/api/v4/phone_number_service/physical_phones/${phoneId}`,
    {
      method: API_METHODS.PUT,
      headers: apiHeaders(store.getState()),
      body: JSON.stringify(updates),
    }
  )
  response = handleErrors(response)
  return response.json()
}

// Function to get existing Physical Phones for an Ansible
const getExistingPhones = async (
  ansibleId: string
): Promise<PhysicalPhone[]> => {
  let response = await fetch(
    `${BASE_API_URL}/api/v4/phone_number_service/physical_phones?ansibleId=${ansibleId}&include=ansible,macMiniUser`,
    {
      method: API_METHODS.GET,
      headers: apiHeaders(store.getState()),
    }
  )
  response = handleErrors(response)
  const data = await response.json()
  return data.data || []
}

const usePhysicalPhoneAssignmentWithNaming =
  (): UsePhysicalPhoneAssignmentReturn => {
    const [isAssigning, setIsAssigning] = useState(false)
    const [assignmentProgress, setAssignmentProgress] = useState<{
      current: number
      total: number
      currentAction: string
      previewNames?: string[]
    } | null>(null)

    const queryClient = useQueryClient()

    const assignPhonesToAnsible = useCallback(
      async (phones: PhysicalPhone[], ansible: Ansible, terminalId: string) => {
        if (phones.length === 0) {
          toast.error('No Physical Phones selected for assignment')
          return
        }

        setIsAssigning(true)

        try {
          // Step 1: Get existing Physical Phones for this Ansible to determine naming
          setAssignmentProgress({
            current: 0,
            total: phones.length + 1,
            currentAction: 'Fetching existing Physical Phones for naming...',
          })

          const existingPhones = await getExistingPhones(ansible.id)

          // Step 2: Generate names for all Physical Phones
          const terminalName = ansible.terminal?.name || 'T000'
          const highestPhoneNumber = findHighestPhoneNumber(existingPhones)

          const assignmentData: PhysicalPhoneAssignmentData[] = phones.map(
            (phone, index) => {
              const newPhoneNumber = highestPhoneNumber + index + 1
              const assignedMacMiniNumber =
                calculateMacMiniNumber(newPhoneNumber)

              return {
                phone,
                newName: generatePhoneName(
                  terminalName,
                  ansible.name,
                  newPhoneNumber
                ),
                assignedMacMiniNumber,
                ansible,
              }
            }
          )

          // Show preview of names
          setAssignmentProgress((prev) =>
            prev
              ? {
                  ...prev,
                  previewNames: assignmentData.map(
                    (item) =>
                      `${item.phone.name} → ${item.newName} (Mac Mini ${item.assignedMacMiniNumber})`
                  ),
                }
              : null
          )

          // Step 3: Update each Physical Phone with new name, status, and assignment
          const updatePromises = assignmentData.map(
            (item, index) =>
              new Promise<void>((resolve, reject) => {
                const delay = index * 100 // Stagger requests by 100ms
                setTimeout(async () => {
                  try {
                    const { phone, newName } = item

                    setAssignmentProgress({
                      current: index + 1,
                      total: phones.length + 1,
                      currentAction: `Updating ${phone.name} → ${newName}...`,
                      previewNames: assignmentData.map(
                        (data) =>
                          `${data.phone.name} → ${data.newName} (Mac Mini ${data.assignedMacMiniNumber})`
                      ),
                    })

                    await updatePhysicalPhone(phone.id, {
                      name: newName,
                      status: PhoneStatus.AVAILABLE, // RECEIVED → AVAILABLE when assigned
                      ansibleId: ansible.id,
                    })

                    resolve()
                  } catch (error) {
                    reject(error)
                  }
                }, delay)
              })
          )

          await Promise.all(updatePromises)

          // Step 4: Invalidate related queries to refresh UI
          setAssignmentProgress({
            current: phones.length + 1,
            total: phones.length + 1,
            currentAction: 'Refreshing data...',
          })

          // Invalidate all related queries
          queryClient.invalidateQueries(['received-physical-phones'])
          queryClient.invalidateQueries(['physical-phones-by-ansible'])
          queryClient.invalidateQueries(['ansible-detail', ansible.id])
          queryClient.invalidateQueries(['terminal-detail', terminalId])

          toast.success(
            `Successfully assigned ${phones.length} Physical Phone${
              phones.length === 1 ? '' : 's'
            } to ${ansible.name}`
          )
        } catch (error) {
          console.error('Physical Phone assignment failed:', error)
          toast.error(
            `Assignment failed: ${
              error instanceof Error ? error.message : 'Unknown error'
            }`
          )
          throw error
        } finally {
          setIsAssigning(false)
          setAssignmentProgress(null)
        }
      },
      [queryClient]
    )

    return {
      assignPhonesToAnsible,
      isAssigning,
      assignmentProgress,
    }
  }

export default usePhysicalPhoneAssignmentWithNaming
